import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client.js';
import PageHeader from '../components/PageHeader.jsx';
import LoadingState from '../components/LoadingState.jsx';
import EmptyState from '../components/EmptyState.jsx';

/**
 * Reporting Feeds -- whether each partner's reporting import is actually working.
 *
 * The question this page exists to answer used to have no surface at all.
 * `demand_partner_reported_stats.fetched_at` was written on every upsert since migration 033 and
 * read by nothing; sync outcomes were a console.log; and the Worker cron never throws, by
 * design. So "this partner reports zero" and "nothing has successfully read this partner since
 * September" looked identical from every screen in the product, and the second one could run for
 * a month without anyone noticing.
 *
 * Three things, in the order an operator needs them:
 *   1. per-partner freshness and the partner's own error text
 *   2. the last few sync runs, including ones that DIED (status 'running', no finish time)
 *   3. revisions worth a human's attention -- figures that went DOWN, or stopped being readable
 */

const STALENESS = {
  fresh: { cls: 'active', label: 'Fresh' },
  stale: { cls: 'error', label: 'Stale' },
  never_succeeded: { cls: 'error', label: 'Never succeeded' },
  // Not a failure. No cadence has been agreed with any of these partners, so "late" is a
  // question the data cannot answer -- shown as a configuration gap, not an alarm.
  none_set: { cls: 'neutral', label: 'No cadence set' },
};

const RUN_STATUS = {
  ok: 'active', partial: 'paused', failed: 'error', running: 'neutral',
};

function age(hours) {
  if (hours === null || hours === undefined) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m ago`;
  if (hours < 48) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function when(ts) {
  return ts ? new Date(ts).toISOString().replace('T', ' ').slice(0, 16) + 'Z' : '—';
}

export default function ReportingFeedsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.reportingHealth());
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load reporting health.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary;

  return (
    <div className="page">
      <PageHeader
        title="Reporting Feeds"
        subtitle="Whether each demand partner's reporting import is working, when it last succeeded, and what changed under us."
        actions={<button className="btn" onClick={load}>Refresh</button>}
      />

      {loading && <LoadingState label="Loading reporting health…" />}
      {!loading && error && <EmptyState title="Couldn't load reporting health" message={error} />}

      {!loading && !error && data && (
        <>
          <p className="card__hint">
            {s.configured} partner{s.configured === 1 ? '' : 's'} with a reporting connector ·{' '}
            {s.neverSucceeded} never succeeded · {s.stale} stale · {s.withRecentErrors} with
            errors or unreadable fields · {s.noExpectationSet} with no agreed cadence.
            {' '}Staleness is measured from the last <em>successful</em> import, not the last attempt.
          </p>

          <section className="card card--flush">
            <div className="card__header card__header--padded">
              <h2 className="card__title">Partner feeds</h2>
            </div>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Partner</th>
                    <th>State</th>
                    <th title="When an import last returned a usable figure for this partner.">Last success</th>
                    <th title="The most recent report date we hold a usable figure for.">Latest day held</th>
                    <th className="num" title="Rows recorded as an outright failure.">Errors</th>
                    <th className="num" title="Rows where the fetch worked but a figure could not be read, or the currency is unknown. These are NULL, never zero.">Unreadable</th>
                    <th>Last error</th>
                  </tr>
                </thead>
                <tbody>
                  {data.partners.map((p) => {
                    const st = STALENESS[p.staleness] || STALENESS.none_set;
                    return (
                      <tr key={p.code}>
                        <td>
                          <div className="cell-title">{p.name}</div>
                          <div className="cell-subtitle">
                            <code className="code-chip">{p.code}</code> · {p.connectorType}
                            {!p.enabled && ' · disabled'}
                          </div>
                        </td>
                        <td>
                          <span className={`status-badge status-badge--${st.cls}`}>{st.label}</span>
                          {p.expectedLagHours !== null && (
                            <div className="cell-subtitle">expects ≤ {p.expectedLagHours}h</div>
                          )}
                        </td>
                        <td>
                          <div>{age(p.lastSuccessAgeHours)}</div>
                          <div className="cell-subtitle">{when(p.lastSuccessAt)}</div>
                        </td>
                        <td>{p.lastSuccessfulReportDate || <span className="cell-muted">none</span>}</td>
                        <td className="num">{p.rowsError}</td>
                        <td className="num">{p.rowsPartial}</td>
                        <td style={{ maxWidth: '26rem' }}>
                          {p.lastError
                            ? <span className="cell-muted" style={{ wordBreak: 'break-word' }}>{p.lastError}</span>
                            : <span className="cell-muted">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card card--flush">
            <div className="card__header card__header--padded">
              <h2 className="card__title">Recent sync runs</h2>
              <span className="card__hint">
                A run stuck on <strong>running</strong> with no finish time did not fail — it died
                part-way. That is a different problem from a failed run, and from one that never started.
              </span>
            </div>
            {data.recentRuns.length === 0
              ? <EmptyState title="No sync runs recorded yet" message="Runs are recorded from migration 071 onward; earlier syncs left only log lines." />
              : (
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Started</th><th>Status</th><th>Trigger</th><th>Range</th>
                        <th className="num">OK</th><th className="num">Unreadable</th>
                        <th className="num">Errors</th><th className="num">Changed</th>
                        <th className="num" title="Fetches that confirmed what we already held. A high number here is healthy.">Unchanged</th>
                        <th>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentRuns.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <div>{when(r.startedAt)}</div>
                            <div className="cell-subtitle">{r.finishedAt ? `finished ${when(r.finishedAt)}` : 'not finished'}</div>
                          </td>
                          <td><span className={`status-badge status-badge--${RUN_STATUS[r.status] || 'neutral'}`}>{r.status}</span></td>
                          <td>{r.trigger}</td>
                          <td className="cell-muted">{r.range.from} → {r.range.to}{r.partnerCode ? ` · ${r.partnerCode}` : ''}</td>
                          <td className="num">{r.rowsOk}</td>
                          <td className="num">{r.rowsPartial}</td>
                          <td className="num">{r.rowsError}</td>
                          <td className="num">{r.rowsRevised}</td>
                          <td className="num">{r.rowsUnchanged}</td>
                          <td style={{ maxWidth: '22rem' }}>
                            {r.errorSummary
                              ? <span className="cell-muted" style={{ wordBreak: 'break-word' }}>{r.errorSummary}</span>
                              : <span className="cell-muted">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </section>

          <section className="card card--flush">
            <div className="card__header card__header--padded">
              <h2 className="card__title">Revisions worth a look</h2>
              <span className="card__hint">
                Figures that went <strong>down</strong> after having been reported higher, or that
                stopped being readable. Ordinary restatements are not listed — several partners
                revise closed days by design. Nothing here is auto-corrected.
              </span>
            </div>
            {data.flaggedRevisions.length === 0
              ? <EmptyState title="Nothing flagged in the last 30 days" message="No partner figure has decreased or become unreadable since the history started being kept." />
              : (
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Partner</th><th>Day</th><th>What changed</th>
                        <th className="num">Revenue before → after</th>
                        <th className="num">Impressions before → after</th>
                        <th>Recorded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.flaggedRevisions.map((r) => (
                        <tr key={`${r.partnerCode}-${r.reportDate}-${r.revision}`}>
                          <td><code className="code-chip">{r.partnerCode}</code></td>
                          <td>{r.reportDate}</td>
                          <td>
                            <span className={`status-badge status-badge--${r.changeKind === 'lost' ? 'error' : 'paused'}`}>
                              {r.changeKind}
                            </span>
                            <div className="cell-subtitle">rev {r.revision}</div>
                          </td>
                          <td className="num">
                            {r.previousRevenue ?? '—'} → {r.revenue ?? '—'}
                          </td>
                          <td className="num">
                            {r.previousImpressions ?? '—'} → {r.impressions ?? '—'}
                          </td>
                          <td className="cell-muted">{when(r.recordedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </section>
        </>
      )}
    </div>
  );
}
